import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Factory, ShieldCheck, Globe, ChevronRight, Cpu, Zap, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS, scrollToSection } from '@/lib/index';
import { productionLines, coatingFacilities, productCategories, companyStats } from '@/data/index';
import { ProductionLineCard, CoatingFacilityCard, ProductCard, StatsCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { Button } from '@/components/ui/button';
import { LMEWidget } from '@/components/LMEWidget';

export default function HomeEs() {
  return (
    <div className="flex flex-col w-full overflow-hidden">
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] w-full flex items-center">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.HERO_INDUSTRIAL_4} alt="Fábrica Industrial de Aluminio" className="w-full h-full object-cover brightness-[0.4]" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-primary/40 to-transparent" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={springPresets.smooth} 
            className="max-w-3xl text-white"
          >
            <span className="inline-block py-1 px-3 rounded-sm bg-accent/20 border border-accent/30 text-accent text-sm font-medium mb-6 uppercase tracking-widest">
              Tecnologías de Aluminio del Futuro
            </span>
            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
              Poder de <br />
              <span className="text-accent">Producción Integrada</span> que Desafía Límites
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-10 max-w-2xl font-light">
              Desde sistemas arquitectónicos hasta perfiles industriales, producimos con estándares globales con nuestras líneas de extrusión de 4" - 5" - 6" - 7"- 8" y instalaciones de pintura de alta capacidad.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-primary font-bold px-8 h-14">
                <Link to={ROUTE_PATHS.CONTACT_ES}>
                  Solicitar Cotización
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-8 h-14" onClick={() => scrollToSection('integrated-production')}>
                Descubrir Producción
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Floating Stats Quick View */}
        <div className="absolute bottom-0 left-0 w-full bg-white/5 backdrop-blur-md border-t border-white/10 hidden lg:block">
          <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-4 gap-8">
              {companyStats.map(stat => (
                <div key={stat.id} className="text-white">
                  <p className="text-3xl font-bold text-accent">{stat.value}{stat.suffix}</p>
                  <p className="text-sm text-gray-400 font-medium uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LME Widget */}
      <section className="py-12 bg-muted/30">
        <div className="container mx-auto px-4">
          <LMEWidget />
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial="hidden" 
              whileInView="visible" 
              viewport={{ once: true }} 
              variants={fadeInUp}
            >
              <div className="flex items-center gap-2 text-primary font-bold mb-4 uppercase tracking-tighter">
                <div className="h-1 w-12 bg-primary" />
                Visión Corporativa
              </div>
              <h2 className="text-4xl font-bold mb-8 text-primary">
                Medio Siglo de Experiencia, <br />
                Enfoque de Ingeniería Moderna
              </h2>
              <div className="space-y-6 text-muted-foreground leading-relaxed">
                <p>
                  Desde 1980, continuamos nuestro camino como una de las organizaciones pioneras de Turquía en el sector de extrusión de aluminio. En nuestras instalaciones construidas sobre 26.000 m² de área cerrada, reunimos todos los procesos desde la materia prima hasta el producto final bajo un mismo techo.
                </p>
                <p>
                  No solo producimos perfiles, ofrecemos soluciones de ingeniería. Con nuestras unidades de diseño de moldes, fundición, extrusión, tratamiento superficial y procesamiento mecánico, respondemos a las necesidades más complejas de nuestros clientes.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6 mt-10">
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-primary/5 rounded-lg">
                    <ShieldCheck className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-primary">Alta Calidad</h4>
                    <p className="text-sm text-muted-foreground">Estándares Qualicoat e ISO</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-primary/5 rounded-lg">
                    <Globe className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-primary">Alcance Global</h4>
                    <p className="text-sm text-muted-foreground">Exportación a más de 45 países</p>
                  </div>
                </div>
              </div>
              <Button asChild variant="link" className="mt-8 p-0 text-primary font-bold hover:no-underline">
                <Link to={ROUTE_PATHS.COMPANY_ES} className="flex items-center">
                  Examinar Perfil de la Empresa <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              whileInView={{ opacity: 1, scale: 1 }} 
              viewport={{ once: true }} 
              className="relative"
            >
              <div className="aspect-video rounded-xl overflow-hidden shadow-2xl">
                <img src="/images/Gemini_Generated_Image_wl5sy6wl5sy6wl5s.png" alt="Línea de Extrusión" className="w-full h-[333px] object-cover" />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-primary p-10 rounded-xl text-white hidden md:block">
                <div className="text-5xl font-bold text-accent mb-2">40+</div>
                <div className="text-sm font-medium uppercase tracking-widest opacity-80">Años de Experiencia <br /> en el Sector</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integrated Production (Extrusion) */}
      <section id="integrated-production" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="mb-16">
            <div className="flex items-center gap-2 text-primary font-bold mb-4 uppercase tracking-tighter">
              <div className="h-1 w-12 bg-primary" />
              Tecnologías de Producción
            </div>
            <h2 className="text-4xl font-bold text-primary">Líneas de Extrusión Integradas</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl">
              Con nuestras 5 líneas de extrusión modernas de diferentes escalas, producimos perfiles adecuados para todo tipo de requisitos técnicos y estéticos.
            </p>
          </div>

          <motion.div 
            variants={staggerContainer} 
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true, margin: "-100px" }} 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6"
          >
            {productionLines.map(line => (
              <motion.div key={line.id} variants={staggerItem}>
                <ProductionLineCard line={line} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Coating Facilities */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-16 mb-20">
            <div className="w-full lg:w-1/3">
              <div className="flex items-center gap-2 text-primary font-bold mb-4 uppercase tracking-tighter">
                <div className="h-1 w-12 bg-primary" />
                Tratamiento Superficial
              </div>
              <h2 className="text-4xl font-bold text-primary mb-6">Instalaciones de Tratamiento Superficial y Pintura</h2>
              <p className="text-muted-foreground mb-8">
                Con nuestras 2 líneas de pintura en polvo horizontales y 1 vertical, tenemos una capacidad de pintura de más de 21.000 toneladas anuales. Ofrecemos opciones de color ilimitadas con estándares Qualicoat Seaside.
              </p>
              <ul className="space-y-4">
                {['2 Líneas de Pintura en Polvo Horizontales', '1 Línea de Pintura en Polvo Vertical', 'Preparación Superficial Sin Cromato', 'Amplia Gama de Opciones RAL'].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 font-medium text-primary">
                    <div className="h-2 w-2 rounded-full bg-accent" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full lg:w-2/3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {coatingFacilities.map(facility => (
                  <CoatingFacilityCard key={facility.id} facility={facility} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-24 bg-primary text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10 pointer-events-none">
          <Layers className="w-full h-full stroke-[0.5]" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Nuestros Grupos de Productos</h2>
            <p className="text-primary-foreground/70 max-w-2xl mx-auto">
              En el punto donde se encuentran la ingeniería y la estética, producimos soluciones de aluminio específicas para su proyecto.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {productCategories.map(category => (
              <ProductCard key={category.id} category={category} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-accent">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="text-primary">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">¿Necesita Soporte Técnico para su Proyecto?</h2>
              <p className="text-lg font-medium opacity-80">
                Nuestro equipo de ingeniería experto está con usted desde la etapa de diseño hasta la producción.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-primary text-white hover:bg-primary/90 px-10 h-16 text-lg">
                <Link to={ROUTE_PATHS.CONTACT_ES}>Solicitar Cotización</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary/5 px-10 h-16 text-lg">
                <Link to={ROUTE_PATHS.DOWNLOAD_CENTER_ES}>Descargar Catálogos</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability Highlight */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center p-4 bg-green-50 rounded-full mb-8">
            <Zap className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-primary mb-6">Futuro 100% Reciclable</h2>
          <p className="text-muted-foreground max-w-3xl mx-auto mb-10 text-lg">
            El aluminio se puede reciclar infinitamente. En nuestras instalaciones de producción, utilizando tecnologías respetuosas con el medio ambiente, minimizamos nuestra huella de carbono y producimos aluminio verde para un mundo sostenible.
          </p>
          <Button asChild variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
            <Link to={ROUTE_PATHS.SUSTAINABILITY_ES}>Nuestra Visión de Sostenibilidad</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}